import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState, useEffect, useCallback, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, CheckCircle2, AlertTriangle, ExternalLink, Save, Trash2, RefreshCw, Plus, Sparkles, Wand2, ImageIcon } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { KNOWN_ROUTES } from "@/lib/known-routes";
import {
  listSeoOverrides,
  upsertSeoOverride,
  deleteSeoOverride,
} from "@/lib/seo-overrides.functions";
import { analyzeSeoPage, bulkOptimizeSeo } from "@/lib/seo-ai.functions";
import { uploadOgImage } from "@/lib/seo-image.functions";
import { BANNER_FIXTURES, parseHtml as parseHtmlShared } from "@/lib/seo-banner";

const SITE = "https://elvion-group.nl";

type Live = {
  status?: number;
  title?: string;
  description?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  robots?: string;
  h1?: string;
  bannerImages?: string[];
  error?: string;
};

export const Route = createFileRoute("/_authenticated/admin/seo")({
  head: () => ({
    meta: [
      { title: "Admin — SEO beheer" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AdminSeoPage,
});

const parseHtml = parseHtmlShared;


function lengthHint(value: string | undefined, min: number, max: number) {
  if (!value) return null;
  const len = value.length;
  const ok = len >= min && len <= max;
  return (
    <span className={`text-xs ${ok ? "text-emerald-600" : "text-amber-600"}`}>
      {len} tekens (aanbevolen {min}–{max})
    </span>
  );
}

function AdminSeoPage() {
  const { isAdmin, loading: authLoading } = useAuth();
  const qc = useQueryClient();

  const listFn = useServerFn(listSeoOverrides);
  const upsertFn = useServerFn(upsertSeoOverride);
  const deleteFn = useServerFn(deleteSeoOverride);
  const analyzeFn = useServerFn(analyzeSeoPage);
  const bulkFn = useServerFn(bulkOptimizeSeo);
  const uploadOgFn = useServerFn(uploadOgImage);
  const [aiSuggestion, setAiSuggestion] = useState<any>(null);
  const [bulkProgress, setBulkProgress] = useState<{ done: number; total: number } | null>(null);
  const [bulkResults, setBulkResults] = useState<any[] | null>(null);
  const [ogUploading, setOgUploading] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["seo-overrides"],
    queryFn: () => listFn({}),
    enabled: isAdmin,
  });

  const [base, setBase] = useState(SITE);
  const [selectedPath, setSelectedPath] = useState<string>("/");
  const [customPath, setCustomPath] = useState("");
  const [form, setForm] = useState({
    title: "",
    description: "",
    og_title: "",
    og_description: "",
    og_image: "",
    canonical: "",
    robots: "",
    json_ld: "",
  });
  const [live, setLive] = useState<Live | null>(null);
  const [liveLoading, setLiveLoading] = useState(false);

  const overrides = (data?.overrides ?? []) as any[];

  const allPaths = useMemo(() => {
    const map = new Map<string, string>();
    for (const r of KNOWN_ROUTES) map.set(r.path, r.label);
    for (const o of overrides) if (!map.has(o.path)) map.set(o.path, o.path);
    return Array.from(map.entries()).map(([path, label]) => ({ path, label }));
  }, [overrides]);

  const currentOverride = overrides.find((o) => o.path === selectedPath);

  useEffect(() => {
    setForm({
      title: currentOverride?.title ?? "",
      description: currentOverride?.description ?? "",
      og_title: currentOverride?.og_title ?? "",
      og_description: currentOverride?.og_description ?? "",
      og_image: currentOverride?.og_image ?? "",
      canonical: currentOverride?.canonical ?? "",
      robots: currentOverride?.robots ?? "",
      json_ld: currentOverride?.json_ld ? JSON.stringify(currentOverride.json_ld, null, 2) : "",
    });
    setLive(null);
    setAiSuggestion(null);
  }, [selectedPath, currentOverride?.updated_at]);

  async function fetchLive() {
    setLiveLoading(true);
    setLive(null);
    try {
      const url = base.replace(/\/$/, "") + (selectedPath.startsWith("/") ? selectedPath : `/${selectedPath}`);
      const res = await fetch(url);
      const html = await res.text();
      const parsed = parseHtml(html, url);
      setLive({ status: res.status, ...parsed });
      // Auto-vul OG image vanuit banner als gebruiker niets ingevuld heeft en geen override bestaat
      setForm((prev) => {
        if (prev.og_image.trim()) return prev;
        const pick = parsed.ogImage || parsed.bannerImages?.[0];
        return pick ? { ...prev, og_image: pick } : prev;
      });
    } catch (e) {
      setLive({ error: e instanceof Error ? e.message : "Fout" });
    } finally {
      setLiveLoading(false);
    }
  }


  const saveMut = useMutation({
    mutationFn: async () => {
      let jsonLdParsed: any = null;
      if (form.json_ld.trim()) {
        try {
          jsonLdParsed = JSON.parse(form.json_ld);
        } catch {
          throw new Error("JSON-LD is geen geldige JSON");
        }
      }
      return upsertFn({
        data: {
          path: selectedPath,
          title: form.title.trim() || null,
          description: form.description.trim() || null,
          og_title: form.og_title.trim() || null,
          og_description: form.og_description.trim() || null,
          og_image: form.og_image.trim() || null,
          canonical: form.canonical.trim() || null,
          robots: form.robots.trim() || null,
          json_ld: jsonLdParsed,
        },
      });
    },
    onSuccess: () => {
      toast.success("Opgeslagen");
      qc.invalidateQueries({ queryKey: ["seo-overrides"] });
      qc.invalidateQueries({ queryKey: ["seo-overrides-public"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteMut = useMutation({
    mutationFn: () => deleteFn({ data: { path: selectedPath } }),
    onSuccess: () => {
      toast.success("Override verwijderd");
      qc.invalidateQueries({ queryKey: ["seo-overrides"] });
      qc.invalidateQueries({ queryKey: ["seo-overrides-public"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const analyzeMut = useMutation({
    mutationFn: () => analyzeFn({ data: { path: selectedPath, base } }),
    onSuccess: (res: any) => {
      setAiSuggestion(res.suggestion);
      toast.success("AI suggestie klaar");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  function applySuggestion(s: any) {
    setForm((f) => ({
      ...f,
      title: s.title ?? f.title,
      description: s.description ?? f.description,
      og_title: s.og_title ?? f.og_title,
      og_description: s.og_description ?? f.og_description,
    }));
    toast.success("Suggestie ingevuld — controleer en sla op");
  }

  const bulkMut = useMutation({
    mutationFn: async (apply: boolean) => {
      const paths = KNOWN_ROUTES.map((r) => r.path);
      setBulkProgress({ done: 0, total: paths.length });
      setBulkResults(null);
      // chunk in groups of 5 so we can show progress
      const chunkSize = 5;
      const all: any[] = [];
      for (let i = 0; i < paths.length; i += chunkSize) {
        const chunk = paths.slice(i, i + chunkSize);
        const res: any = await bulkFn({ data: { paths: chunk, base, apply } });
        all.push(...res.results);
        setBulkProgress({ done: Math.min(i + chunkSize, paths.length), total: paths.length });
      }
      return { results: all, applied: apply };
    },
    onSuccess: (res: any) => {
      setBulkResults(res.results);
      setBulkProgress(null);
      const okCount = res.results.filter((r: any) => r.ok).length;
      toast.success(
        res.applied
          ? `${okCount} pagina's geoptimaliseerd en opgeslagen`
          : `${okCount} suggesties opgehaald`,
      );
      if (res.applied) {
        qc.invalidateQueries({ queryKey: ["seo-overrides"] });
        qc.invalidateQueries({ queryKey: ["seo-overrides-public"] });
      }
    },
    onError: (e: Error) => {
      setBulkProgress(null);
      toast.error(e.message);
    },
  });

  function addCustom() {
    const p = customPath.trim();
    if (!p.startsWith("/")) {
      toast.error("Pad moet beginnen met /");
      return;
    }
    setSelectedPath(p);
    setCustomPath("");
  }

  const uploadOgFile = useCallback(
    async (file: File, successMsg: string) => {
      if (!file.type.startsWith("image/")) {
        toast.error("Alleen afbeeldingen zijn toegestaan");
        return;
      }
      setOgUploading(true);
      try {
        const buf = await file.arrayBuffer();
        const base64 = btoa(
          new Uint8Array(buf).reduce((data, byte) => data + String.fromCharCode(byte), "")
        );
        const res = await uploadOgFn({
          data: {
            dataBase64: base64,
            filename: file.name || "pasted-image.png",
            contentType: file.type,
          },
        });
        setForm((prev) => ({ ...prev, og_image: res.url }));
        toast.success(successMsg);
      } catch (err: any) {
        toast.error(err.message || "Upload mislukt");
      } finally {
        setOgUploading(false);
      }
    },
    [uploadOgFn]
  );

  const handleOgPaste = useCallback(
    async (e: React.ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      const imageItem = Array.from(items).find((it) => it.type.startsWith("image/"));
      if (!imageItem) return;
      e.preventDefault();
      const file = imageItem.getAsFile();
      if (!file) return;
      await uploadOgFile(file, "Afbeelding geplakt en opgeslagen");
    },
    [uploadOgFile]
  );

  const [ogDragOver, setOgDragOver] = useState(false);
  const handleOgDragOver = useCallback((e: React.DragEvent) => {
    if (!Array.from(e.dataTransfer.types).includes("Files")) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
    setOgDragOver(true);
  }, []);
  const handleOgDragLeave = useCallback((e: React.DragEvent) => {
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setOgDragOver(false);
  }, []);
  const handleOgDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      setOgDragOver(false);
      const file = Array.from(e.dataTransfer.files ?? []).find((f) =>
        f.type.startsWith("image/")
      );
      if (!file) {
        toast.error("Sleep een afbeeldingsbestand");
        return;
      }
      await uploadOgFile(file, "Afbeelding gesleept en opgeslagen");
    },
    [uploadOgFile]
  );

  const handleOgFileInput = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = "";
      if (!file) return;
      await uploadOgFile(file, "Afbeelding geüpload");
    },
    [uploadOgFile]
  );

  if (authLoading) return <p>Laden…</p>;
  if (!isAdmin) return <p className="text-destructive">Geen toegang.</p>;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">SEO beheer</h1>
          <p className="text-sm text-muted-foreground">
            Bekijk en pas per pagina de SEO en metadata aan. Wijzigingen worden direct toegepast op de live site.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => bulkMut.mutate(false)}
            disabled={bulkMut.isPending}
          >
            <Sparkles className="mr-1 h-4 w-4" />
            AI scan alle pagina's
          </Button>
          <Button
            size="sm"
            onClick={() => {
              if (confirm("AI laat alle bekende pagina's analyseren en slaat de geoptimaliseerde meta direct op. Doorgaan?")) {
                bulkMut.mutate(true);
              }
            }}
            disabled={bulkMut.isPending}
          >
            <Wand2 className="mr-1 h-4 w-4" />
            AI optimaliseer & sla op
          </Button>
        </div>
      </div>

      {bulkProgress && (
        <div className="rounded-md border bg-muted/30 p-3 text-sm">
          <div className="flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            AI bezig… {bulkProgress.done}/{bulkProgress.total} pagina's
          </div>
          <div className="mt-2 h-1.5 w-full overflow-hidden rounded bg-muted">
            <div
              className="h-full bg-primary transition-all"
              style={{ width: `${(bulkProgress.done / bulkProgress.total) * 100}%` }}
            />
          </div>
        </div>
      )}

      {bulkResults && !bulkProgress && (
        <div className="rounded-md border bg-card p-3 text-sm">
          <div className="mb-2 font-semibold">AI bulk resultaten ({bulkResults.length})</div>
          <ul className="max-h-64 space-y-1 overflow-y-auto text-xs">
            {bulkResults.map((r, i) => (
              <li key={i} className="flex items-start justify-between gap-2 border-b py-1 last:border-0">
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-[11px] text-muted-foreground">{r.path}</div>
                  {r.ok ? (
                    <div className="truncate"><strong>{r.suggestion?.title}</strong></div>
                  ) : (
                    <div className="text-destructive">{r.error}</div>
                  )}
                </div>
                {r.ok && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 text-xs"
                    onClick={() => {
                      setSelectedPath(r.path);
                      setAiSuggestion(r.suggestion);
                    }}
                  >
                    Bekijk
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}



      <div className="grid gap-4 lg:grid-cols-[280px,1fr]">
        {/* Pages sidebar */}
        <aside className="space-y-3 rounded-lg border bg-card p-3">
          <div className="space-y-1">
            <Label className="text-xs">Basis URL</Label>
            <Input value={base} onChange={(e) => setBase(e.target.value)} className="h-8 text-sm" />
          </div>

          <div className="flex gap-1">
            <Input
              placeholder="/eigen-pad"
              value={customPath}
              onChange={(e) => setCustomPath(e.target.value)}
              className="h-8 text-sm"
              onKeyDown={(e) => e.key === "Enter" && addCustom()}
            />
            <Button size="sm" variant="outline" onClick={addCustom}>
              <Plus className="h-4 w-4" />
            </Button>
          </div>

          <div className="max-h-[60vh] overflow-y-auto pr-1">
            {isLoading ? (
              <p className="text-sm text-muted-foreground">Laden…</p>
            ) : (
              <ul className="space-y-1">
                {allPaths.map((r) => {
                  const has = overrides.some((o) => o.path === r.path);
                  const active = selectedPath === r.path;
                  return (
                    <li key={r.path}>
                      <button
                        onClick={() => setSelectedPath(r.path)}
                        className={`flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-sm transition ${
                          active ? "bg-primary text-primary-foreground" : "hover:bg-muted"
                        }`}
                      >
                        <span className="truncate">
                          <span className="font-medium">{r.label}</span>
                          <span className={`ml-1 text-xs ${active ? "opacity-80" : "text-muted-foreground"}`}>
                            {r.path}
                          </span>
                        </span>
                        {has && (
                          <CheckCircle2 className={`h-3 w-3 shrink-0 ${active ? "" : "text-emerald-600"}`} />
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </aside>

        {/* Editor */}
        <div className="space-y-4">
          <div className="rounded-lg border bg-card p-4">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <div>
                <div className="text-xs uppercase text-muted-foreground">Geselecteerde pagina</div>
                <a
                  href={`${base.replace(/\/$/, "")}${selectedPath}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-sm font-semibold hover:underline"
                >
                  {selectedPath} <ExternalLink className="h-3 w-3" />
                </a>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={fetchLive} disabled={liveLoading}>
                  {liveLoading ? (
                    <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="mr-1 h-4 w-4" />
                  )}
                  Live ophalen
                </Button>
                <select
                  className="h-9 rounded-md border bg-background px-2 text-xs"
                  defaultValue=""
                  onChange={(e) => {
                    const fx = BANNER_FIXTURES.find((f) => f.id === e.target.value);
                    if (!fx) return;
                    const parsed = parseHtml(fx.html, fx.pageUrl);
                    setLive({ status: 200, ...parsed });
                    setForm((prev) => {
                      if (prev.og_image.trim()) return prev;
                      const pick = parsed.ogImage || parsed.bannerImages?.[0];
                      return pick ? { ...prev, og_image: pick } : prev;
                    });
                    e.target.value = "";
                  }}
                >
                  <option value="" disabled>Voorbeeld laden…</option>
                  {BANNER_FIXTURES.map((f) => (
                    <option key={f.id} value={f.id}>{f.label}</option>
                  ))}
                </select>
                <Button size="sm" onClick={() => analyzeMut.mutate()} disabled={analyzeMut.isPending}>
                  {analyzeMut.isPending ? (
                    <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                  ) : (
                    <Sparkles className="mr-1 h-4 w-4" />
                  )}
                  AI optimaliseer
                </Button>
              </div>
            </div>

            {aiSuggestion && (
              <div className="mb-3 rounded-md border border-primary/30 bg-primary/5 p-3 text-xs">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1 font-semibold text-primary">
                    <Sparkles className="h-3.5 w-3.5" /> AI suggestie
                  </div>
                  <Button size="sm" className="h-7" onClick={() => applySuggestion(aiSuggestion)}>
                    Toepassen op velden
                  </Button>
                </div>
                <div className="space-y-1">
                  <div><span className="font-semibold">Title:</span> {aiSuggestion.title}</div>
                  <div><span className="font-semibold">Description:</span> {aiSuggestion.description}</div>
                  <div><span className="font-semibold">OG Title:</span> {aiSuggestion.og_title}</div>
                  <div><span className="font-semibold">OG Description:</span> {aiSuggestion.og_description}</div>
                  {aiSuggestion.target_keywords?.length > 0 && (
                    <div>
                      <span className="font-semibold">Doel-zoekwoorden:</span>{" "}
                      {aiSuggestion.target_keywords.map((k: string) => (
                        <span key={k} className="mr-1 inline-block rounded bg-primary/10 px-1.5 py-0.5 text-[10px]">{k}</span>
                      ))}
                    </div>
                  )}
                  {aiSuggestion.current_keywords_guess?.length > 0 && (
                    <div>
                      <span className="font-semibold">Wordt nu gevonden op:</span>{" "}
                      {aiSuggestion.current_keywords_guess.map((k: string) => (
                        <span key={k} className="mr-1 inline-block rounded bg-muted px-1.5 py-0.5 text-[10px]">{k}</span>
                      ))}
                    </div>
                  )}
                  {aiSuggestion.reason && (
                    <div className="mt-1 text-muted-foreground"><span className="font-semibold">Waarom:</span> {aiSuggestion.reason}</div>
                  )}
                  {aiSuggestion.improvements?.length > 0 && (
                    <ul className="mt-1 list-disc pl-4 text-muted-foreground">
                      {aiSuggestion.improvements.map((i: string, idx: number) => <li key={idx}>{i}</li>)}
                    </ul>
                  )}
                </div>
              </div>
            )}

            {live && (
              <div className="mb-3 rounded-md border bg-muted/30 p-3 text-xs">
                {live.error ? (
                  <p className="text-destructive">{live.error}</p>
                ) : (
                  <div className="grid gap-1 md:grid-cols-2">
                    <div><span className="font-semibold">HTTP:</span> {live.status}</div>
                    <div><span className="font-semibold">H1:</span> {live.h1 ?? "—"}</div>
                    <div className="md:col-span-2"><span className="font-semibold">Title:</span> {live.title ?? "—"}</div>
                    <div className="md:col-span-2"><span className="font-semibold">Description:</span> {live.description ?? "—"}</div>
                    <div><span className="font-semibold">Canonical:</span> {live.canonical ?? "—"}</div>
                    <div><span className="font-semibold">Robots:</span> {live.robots ?? "—"}</div>
                    <div><span className="font-semibold">OG Title:</span> {live.ogTitle ?? "—"}</div>
                    <div><span className="font-semibold">OG Image:</span> {live.ogImage ?? "—"}</div>
                  </div>
                )}
              </div>
            )}

            <div className="space-y-3">
              <Field
                label="Title"
                value={form.title}
                onChange={(v) => setForm({ ...form, title: v })}
                hint={lengthHint(form.title, 30, 60)}
                placeholder="Pagina titel — Elvion Group Nederland"
              />
              <FieldArea
                label="Description"
                value={form.description}
                onChange={(v) => setForm({ ...form, description: v })}
                hint={lengthHint(form.description, 70, 160)}
                rows={2}
              />
              <div className="grid gap-3 md:grid-cols-2">
                <Field
                  label="OG Title"
                  value={form.og_title}
                  onChange={(v) => setForm({ ...form, og_title: v })}
                  placeholder="Valt terug op Title"
                />
                <div
                  className={`relative space-y-2 rounded-lg border-2 border-dashed p-2 transition-colors focus-within:border-primary/30 ${
                    ogDragOver
                      ? "border-primary bg-primary/5"
                      : "border-transparent hover:border-muted-foreground/20"
                  }`}
                  onPaste={handleOgPaste}
                  onDragOver={handleOgDragOver}
                  onDragEnter={handleOgDragOver}
                  onDragLeave={handleOgDragLeave}
                  onDrop={handleOgDrop}
                  tabIndex={0}
                >
                  <div className="flex items-center justify-between">
                    <Field
                      label="OG Image (volledige URL)"
                      value={form.og_image}
                      onChange={(v) => setForm({ ...form, og_image: v })}
                      placeholder={live?.bannerImages?.[0] ?? live?.ogImage ?? "Auto: eerste banner van pagina"}
                    />
                  </div>
                  <div className="flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
                    <ImageIcon className="h-3 w-3" />
                    <span>
                      {ogDragOver
                        ? "Laat los om te uploaden…"
                        : "Sleep een afbeelding hierheen, plak met Ctrl+V / ⌘V, of"}
                    </span>
                    {!ogDragOver && (
                      <label className="cursor-pointer underline">
                        kies bestand
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={handleOgFileInput}
                        />
                      </label>
                    )}
                  </div>


                  {/* Altijd zichtbare OG preview, exact 1.91:1 (1200×630) — */}
                  {/* alle bronnen worden via object-cover gesneden zodat het */}
                  {/* nooit vervormt en altijd op OG-maat past. */}
                  {(() => {
                    const effective =
                      form.og_image.trim() ||
                      live?.ogImage ||
                      live?.bannerImages?.[0] ||
                      "";
                    return (
                      <div className="space-y-1">
                        <div className="flex items-center justify-between">
                          <div className="text-xs font-medium">Preview (1200×630)</div>
                          {form.og_image && (
                            <button
                              type="button"
                              onClick={() => setForm({ ...form, og_image: "" })}
                              className="text-xs underline text-muted-foreground"
                            >
                              wissen
                            </button>
                          )}
                        </div>
                        <div
                          className="relative w-full overflow-hidden rounded border bg-muted"
                          style={{ aspectRatio: "1200 / 630" }}
                        >
                          {ogUploading && (
                            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 bg-background/80">
                              <Loader2 className="h-6 w-6 animate-spin text-primary" />
                              <span className="text-xs text-muted-foreground">Afbeelding uploaden…</span>
                            </div>
                          )}
                          {effective ? (
                            <>
                              <img
                                src={effective}
                                alt="OG preview"
                                className="absolute inset-0 h-full w-full object-cover"
                              />
                              <div className="absolute bottom-1 right-1 rounded bg-background/80 px-1.5 py-0.5 text-[10px] text-muted-foreground">
                                cover-crop naar 1.91:1
                              </div>
                            </>
                          ) : (
                            <div className="flex h-full items-center justify-center text-xs text-muted-foreground">
                              Geen afbeelding — kies een banner, laad een voorbeeld, of plak een afbeelding
                            </div>
                          )}
                        </div>
                        {effective && !form.og_image && (
                          <div className="text-[11px] text-muted-foreground">
                            Auto: <span className="break-all">{effective}</span>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {(live?.bannerImages?.length || live?.ogImage) && (
                    <div className="space-y-1">
                      <div className="text-xs text-muted-foreground">
                        Gevonden op pagina (klik om te gebruiken — wordt altijd cover-gesneden naar 1200×630):
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {[live?.ogImage, ...(live?.bannerImages ?? [])]
                          .filter((u, i, a): u is string => !!u && a.indexOf(u) === i)
                          .slice(0, 6)
                          .map((u) => (
                            <button
                              key={u}
                              type="button"
                              onClick={() => setForm({ ...form, og_image: u })}
                              className={`relative overflow-hidden rounded border-2 ${
                                form.og_image === u ? "border-primary" : "border-transparent hover:border-muted-foreground/40"
                              }`}
                              style={{ width: 96, aspectRatio: "1200 / 630" }}
                              title={u}
                            >
                              <img src={u} alt="banner kandidaat" className="absolute inset-0 h-full w-full object-cover" />
                            </button>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <FieldArea
                label="OG Description"
                value={form.og_description}
                onChange={(v) => setForm({ ...form, og_description: v })}
                rows={2}
              />
              <div className="grid gap-3 md:grid-cols-2">
                <Field
                  label="Canonical URL"
                  value={form.canonical}
                  onChange={(v) => setForm({ ...form, canonical: v })}
                  placeholder={`${base}${selectedPath}`}
                />
                <Field
                  label="Robots"
                  value={form.robots}
                  onChange={(v) => setForm({ ...form, robots: v })}
                  placeholder="index, follow  /  noindex, nofollow"
                />
              </div>
              <div>
                <Label className="text-xs">JSON-LD (optioneel)</Label>
                <Textarea
                  value={form.json_ld}
                  onChange={(e) => setForm({ ...form, json_ld: e.target.value })}
                  rows={6}
                  className="font-mono text-xs"
                  placeholder='{"@context":"https://schema.org","@type":"WebPage","name":"..."}'
                />
              </div>
            </div>

            <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t pt-3">
              <div className="text-xs text-muted-foreground">
                {currentOverride ? (
                  <span className="inline-flex items-center gap-1 text-emerald-600">
                    <CheckCircle2 className="h-3 w-3" /> Override actief sinds{" "}
                    {new Date(currentOverride.updated_at).toLocaleString("nl-NL")}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3 text-amber-600" /> Nog geen override — pagina gebruikt standaard meta uit de code
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                {currentOverride && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (confirm("Override verwijderen voor " + selectedPath + "?")) deleteMut.mutate();
                    }}
                    disabled={deleteMut.isPending}
                  >
                    <Trash2 className="mr-1 h-4 w-4" /> Verwijderen
                  </Button>
                )}
                <Button onClick={() => saveMut.mutate()} disabled={saveMut.isPending}>
                  <Save className="mr-1 h-4 w-4" /> {saveMut.isPending ? "Opslaan..." : "Opslaan"}
                </Button>
              </div>
            </div>
          </div>

          <p className="text-xs text-muted-foreground">
            Tip: Title 30–60 tekens, Description 70–160 tekens. Voor Google's index kan het enkele dagen duren voordat wijzigingen zichtbaar zijn.
          </p>
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  hint,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  hint?: React.ReactNode;
  placeholder?: string;
}) {
  return (
    <div>
      <div className="flex items-center justify-between">
        <Label className="text-xs">{label}</Label>
        {hint}
      </div>
      <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
    </div>
  );
}

function FieldArea({
  label,
  value,
  onChange,
  hint,
  rows = 3,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  hint?: React.ReactNode;
  rows?: number;
}) {
  return (
    <div>
      <div className="flex items-center justify-between">
        <Label className="text-xs">{label}</Label>
        {hint}
      </div>
      <Textarea value={value} onChange={(e) => onChange(e.target.value)} rows={rows} />
    </div>
  );
}
